import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Select, InputNumber, message, Tag, Space } from 'antd';
import { DeleteOutlined, ShoppingOutlined } from '@ant-design/icons';
import config from '../../../config';
import {
  getPanier, subscribe, removeLine, setQte, clearPanier, panierTotal, panierCount,
} from '../../varianteProduit/panier';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeEmpty,
} from '../../../components/ndoe';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

/**
 * Page /cart — Panier / checkout.
 *
 * Branchée sur le MÊME panier catalogue que PanierBar (src/pages/varianteProduit/
 * panier.js, localStorage). « Passer à la caisse » réutilise la logique éprouvée
 * de PanierBar : POST commandePost puis une ligne detail-commande par paire.
 * (L'ancien Cart Redux e-commerce, jamais alimenté, est remplacé — pas de second
 * chemin de création de commande.)
 */
const Cart = () => {
  const navigate = useNavigate();
  const userId = useSelector((s) => s.user?.currentUser?.id);
  const [lines, setLines] = useState(getPanier());
  const [clients, setClients] = useState([]);
  const [idClient, setIdClient] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => subscribe(setLines), []);

  const loadClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${DOMAIN}/api/client`);
      setClients(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error('Impossible de charger les clientes.');
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const clientOptions = useMemo(
    () => clients.map((c) => ({
      value: c.id,
      label: [c.nom, c.telephone].filter(Boolean).join(' · '),
    })),
    [clients],
  );

  const total = panierTotal(lines);
  const count = panierCount(lines);

  // Création de commande — logique identique à PanierBar (endpoints existants).
  const passerCaisse = async () => {
    if (!idClient) { message.warning('Choisissez une cliente.'); return; }
    if (!lines.length) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/commande/commandePost`, {
        id_client: idClient,
        user_cr: userId,
      });
      const idCommande = data?.id_commande;
      if (!idCommande) throw new Error('id_commande manquant');

      let ok = 0;
      let dup = 0;
      for (const l of lines) {
        try {
          await axios.post(`${DOMAIN}/api/commande/detail-commande`, {
            id_commande: idCommande,
            id_varianteProduit: l.id_varianteProduit,
            id_taille: l.id_taille,
            prix: l.prix,
            quantite: l.qte,
            user_cr: userId,
          });
          ok += 1;
        } catch (e) {
          if (e?.response?.status === 409) dup += 1;   // déjà dans la commande
          else throw e;
        }
      }

      clearPanier();
      setIdClient(null);
      message.success(`Commande #${idCommande} créée — ${ok} article${ok > 1 ? 's' : ''}${dup ? `, ${dup} déjà présent${dup > 1 ? 's' : ''}` : ''}.`);
      navigate(`/listeDetailView/${idCommande}`);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.response?.data?.error || 'Création de la commande échouée.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Commerce"
        title="Panier"
        subtitle="Finalisez la commande en cours"
        actions={
          lines.length > 0 ? (
            <NdoeActionButton variant="secondary" icon={<DeleteOutlined />} onClick={() => clearPanier()}>
              Vider le panier
            </NdoeActionButton>
          ) : null
        }
      />

      {lines.length === 0 ? (
        <NdoeEmpty
          icon="🛒"
          title="Votre panier est vide"
          text="Ajoutez des paires depuis le catalogue visuel pour créer une commande."
          action={
            <NdoeActionButton variant="primary" icon={<ShoppingOutlined />} onClick={() => navigate('/varianteProduit')}>
              Aller au catalogue
            </NdoeActionButton>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          {/* Lignes du panier */}
          <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {lines.map((l) => (
              <div className="ndoe-mobile-card" key={l.id_varianteProduit}>
                <div className="ndoe-mobile-card__head">
                  <div style={{ minWidth: 0 }}>
                    <h3 className="ndoe-mobile-card__title">{l.nom_produit}</h3>
                    <p className="ndoe-mobile-card__subtitle">
                      <Space size={6} wrap>
                        {l.marque && <span>{l.marque}</span>}
                        {l.taille != null && <Tag>P.{l.taille}</Tag>}
                        {l.couleur && <span style={{ color: 'var(--ndoe-muted)' }}>{l.couleur}</span>}
                      </Space>
                    </p>
                  </div>
                  <span className="ndoe-badge ndoe-badge--gold num">{fmt(Number(l.prix) * Number(l.qte))}</span>
                </div>

                <div className="ndoe-mobile-card__actions" style={{ justifyContent: 'space-between' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--ndoe-muted)' }}>Qté</span>
                    <InputNumber inputMode="decimal"
                      min={1}
                      value={l.qte}
                      onChange={(v) => setQte(l.id_varianteProduit, v)}
                      style={{ width: 72 }}
                    />
                    <span style={{ fontSize: 12, color: 'var(--ndoe-muted)' }}>× {fmt(l.prix)}</span>
                  </span>
                  <NdoeActionButton size="sm" variant="danger" icon={<DeleteOutlined />} onClick={() => removeLine(l.id_varianteProduit)}>
                    Retirer
                  </NdoeActionButton>
                </div>
              </div>
            ))}
          </div>

          {/* Récapitulatif */}
          <div className="ndoe-card" style={{ flex: '0 1 340px', position: 'sticky', top: 16 }}>
            <div className="ndoe-section__title">Récapitulatif de la commande</div>

            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--ndoe-hairline)' }}>
              <span style={{ color: 'var(--ndoe-muted)' }}>Paires</span>
              <b className="num">{count}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontSize: 18 }}>
              <span style={{ color: 'var(--ndoe-ink)', fontWeight: 700 }}>Total</span>
              <b className="num" style={{ color: 'var(--ndoe-ink)' }}>{fmt(total)}</b>
            </div>

            <div style={{ margin: '12px 0' }}>
              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ndoe-muted)', marginBottom: 6 }}>Cliente</label>
              <Select
                showSearch
                allowClear
                placeholder="Sélectionnez une cliente…"
                style={{ width: '100%' }}
                value={idClient}
                onChange={setIdClient}
                options={clientOptions}
                optionFilterProp="label"
                filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
              />
            </div>

            <NdoeActionButton variant="primary" block loading={creating} onClick={passerCaisse}>
              Passer à la caisse
            </NdoeActionButton>
          </div>
        </div>
      )}
    </NdoePage>
  );
};

export default Cart;
